import React, { useEffect } from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import Swal from 'sweetalert2'
import Dropzone, { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import * as Yup from 'yup'
import {
  // updateManualPayment,
  // updateManualPaymentReceiptUpload,
  tenantPaymentUpload,
  getPaymentMethods,
  tenantManualPayment,
} from '../../Property_Api'
import { markAsPaidPropDataType, paymentMethodDataType } from '../RestInfoDataType'

interface valuesDataType {
  payment_method_uuid: string
  payment_reference: string
}

const modalsRoot = document.getElementById('root-modals') || document.body

const MarkAsPaid = ({ rentDetails, show, handleClose, getTenantRentPaymentList }: markAsPaidPropDataType) => {
  const [submitBtnLoading, setSubmitBtnLoading] = useState<boolean>(false)
  const [paymentMethodLoading, setPaymentMethodLoading] = useState<boolean>(true)
  const [paymentMethods, setPaymentMethods] = useState<paymentMethodDataType[]>([])
  const [acceptedRentFiles, setAcceptedRentFiles] = useState<File[]>([])
  const [rejectedRentFiles, setRejectedRentFiles] = useState<FileRejection[]>([])
  const [acceptedUtilityFiles, setAcceptedUtilityFiles] = useState<File[]>([])
  const [rejectedUtilityFiles, setRejectedUtilityFiles] = useState<FileRejection[]>([])
  const [rentReceipt, setRentReceipt] = useState<string>('')
  const [utilityReceipt, setUtilityReceipt] = useState<string>('')

  async function getListPaymentMethods() {
    setPaymentMethodLoading(true)
    try {
      const response = await getPaymentMethods()
      if (response.status === 200) {
        setPaymentMethods(response.data.data)
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response.data.msg.charAt(0).toUpperCase() + response.data.msg.slice(1),
          text: response.data.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    } finally {
      setPaymentMethodLoading(false)
    }
  }

  useEffect(() => {
    getListPaymentMethods()
  }, [])

  const initialValues = {
    payment_method_uuid: '',
    payment_reference: '',
    payment_type: '',
    tenant_utility_invoice_uuid: '',
    tenant_rent_uuid: '',
    invoice_type: ''
  }

  const validationSchema = Yup.object({
    payment_method_uuid: Yup.string().required('Transaction type is required'),
    payment_reference: Yup.string().required('Transaction Reference is required'),
    invoice_type: Yup.string().required('Invoice type is required'),
  })

  const handleFileRead = (file: File, type: string) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result && typeof reader.result === 'string') {
        if (type === "rent")
          setRentReceipt(reader.result.split(',')[1]);
        else if (type === "utility") {
          setUtilityReceipt(reader.result.split(',')[1])
        }
      } else {
        setRentReceipt("");
      }
    };
    reader.readAsDataURL(file);
  };

  const tenantPaymentUploadAPI = async () => {
    try {
      const { data: response2 } = await tenantPaymentUpload(
        rentDetails.property_uuid,
        formik.values.payment_type,
        formik.values.tenant_utility_invoice_uuid,
        formik.values.tenant_rent_uuid,
        rentReceipt,
        utilityReceipt
      )
      if (response2.code !== '200') {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response2.msg.charAt(0).toUpperCase() + response2.msg.slice(1),
          text: response2.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }
  const tenantManualPaymentAPI = async () => {
    try {
      const { data: response2 } = await tenantManualPayment(
        formik.values.payment_type,
        formik.values.tenant_utility_invoice_uuid,
        formik.values.tenant_rent_uuid,
        formik.values.payment_method_uuid,
        formik.values.payment_reference
      )
      if (response2.code !== '200') {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: response2.msg.charAt(0).toUpperCase() + response2.msg.slice(1),
          text: response2.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
      })
    }
  }
  const handleSubmit = async (values: valuesDataType) => {
    setSubmitBtnLoading(true)
    const fetchData = async () => {
      try {
        await Promise.all([
          tenantManualPaymentAPI(),
          (rentReceipt || utilityReceipt) &&
          tenantPaymentUploadAPI()
        ])
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Success',
          text: 'Payment successfully recorded as paid',
          allowOutsideClick: true,
        })
        handleClose()
        getTenantRentPaymentList(rentDetails.property_uuid, rentDetails.property_sub_unit_uuid, true)
        formik.resetForm()
      } catch (error: any) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
        })
      } finally {
        setSubmitBtnLoading(false)
      }
    }
    fetchData()
  }
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => handleSubmit(values),
  })

  const acceptedFormats: Accept = {
    'image/jpeg': [],
    'image/png': [],
    'application/pdf': [],
    'application/msword': [],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
  }

  const handleInvoiceTypeChange = (value: string) => {
    if (value) {
      formik.setFieldValue("invoice_type", value)
      formik.setFieldValue("payment_type", value)
      if (value === "utility" || value === "both") {
        formik.setFieldValue("tenant_utility_invoice_uuid", rentDetails.tenant_utility_invoice_uuid)
      }
      else {
        formik.setFieldValue("tenant_utility_invoice_uuid", "")
      }
      if (value === "rent" || value === "both") {
        formik.setFieldValue("tenant_rent_uuid", rentDetails.tenant_rent_uuid)
      }
      else {
        formik.setFieldValue("tenant_rent_uuid", "")
      }
    }
  }
  const handleDropzoneRent = (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => {
    if (acceptedFiles.length > 0) {
      handleFileRead(acceptedFiles[0], "rent")
    }
    else if (rejectedFiles.length > 0) {
      setRentReceipt('')
    }
    setAcceptedRentFiles(acceptedFiles)
    setRejectedRentFiles(rejectedFiles)
  }
  const handleDropzoneUtility = (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => {
    if (acceptedFiles.length > 0) {
      handleFileRead(acceptedFiles[0], "utility")
    }
    else if (rejectedFiles.length > 0) {
      setUtilityReceipt('')
    }
    setAcceptedUtilityFiles(acceptedFiles)
    setRejectedUtilityFiles(rejectedFiles)
  }
  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-500px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      {/* Begin: Modal header */}
      <div className='modal-header'>
        {/* Begin: Modal Heading */}
        <h2>Mark invoice as Paid</h2>
        {/* End: Modal Heading */}
        {/* Begin::Close button */}
        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <i className='bi bi-x-lg' onClick={handleClose}></i>
        </div>
        {/* end::Close button */}
      </div>
      {/* End: Modal header */}

      {/* Begin: Modal body */}
      <div className='modal-body'>
        {/*Begin::Stepper */}
        {/*Begin:: Form Content */}
        <div className='flex-row-fluid'>
          {/*Begin::Form */}
          <form onSubmit={formik.handleSubmit} className='form'>
            <div className='fv-row'>
              <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                <span className='required'>Invoice type</span>
                <i
                  className='fas fa-exclamation-circle ms-2 fs-7'
                  data-bs-toggle='tooltip'
                  title='Choose invoice type'
                ></i>
              </label>
              <select
                className='form-select form-select-solid'
                placeholder='Select invoice type'
                {...formik.getFieldProps('invoice_type')}
                name='invoice_type'
                onChange={(e) => handleInvoiceTypeChange(e.target.value)}
              >
                <option className='bg-color-gray' value=''>
                  Select invoice type
                </option>
                {
                  (rentDetails.proforma_invoice_link &&
                    (rentDetails.tenant_rent_status_name.toLowerCase() !== 'processing' ||
                      rentDetails.tenant_rent_status_name.toLowerCase() !== 'completed')) &&
                  <option className='bg-color-gray' value='rent'>
                    Rent
                  </option>
                }
                {
                  (rentDetails.utility_invoice_link &&
                    (rentDetails.tenant_utility_status_name.toLowerCase() !== 'processing' ||
                      rentDetails.tenant_utility_status_name.toLowerCase() !== 'completed')) &&
                  <option className='bg-color-gray' value='utility'>
                    Utility
                  </option>
                }
                {
                  (rentDetails.proforma_invoice_link &&
                    rentDetails.utility_invoice_link &&
                    rentDetails.tenant_rent_status_name.toLowerCase() !== 'processing' &&
                    rentDetails.tenant_rent_status_name.toLowerCase() !== 'completed' &&
                    rentDetails.tenant_utility_status_name.toLowerCase() !== 'processing' &&
                    rentDetails.tenant_utility_status_name.toLowerCase() !== 'completed') &&
                  <option className='bg-color-gray' value='both'>
                    Both
                  </option>
                }
              </select>
              <div className='h-20px'>
                {formik.touched.invoice_type && formik.errors.invoice_type && (
                  <div className='fv-help-block color-red'>
                    {formik.errors.invoice_type}
                  </div>
                )}
              </div>
            </div>
            {/*Begin::Form Group Transaction type */}
            {paymentMethodLoading ? (
              <p className='my-10 text-center w-100'>Loading...</p>
            ) : (
              <div className='fv-row'>
                <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                  <span className='required'>Transaction type</span>
                  <i
                    className='fas fa-exclamation-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Choose transaction type'
                  ></i>
                </label>
                <select
                  className='form-select form-select-solid'
                  placeholder='Select transaction type'
                  {...formik.getFieldProps('payment_method_uuid')}
                  name='payment_method_uuid'
                >
                  <option className='bg-color-gray' value=''>
                    Select transaction type
                  </option>
                  {paymentMethods?.map((item: paymentMethodDataType) => {
                    return (
                      <option key={item.payment_method_uuid} value={item.payment_method_uuid}>
                        {item.payment_method_name}
                      </option>
                    )
                  })}
                </select>
                <div className='h-20px'>
                  {formik.touched.payment_method_uuid && formik.errors.payment_method_uuid && (
                    <div className='fv-help-block color-red'>
                      {formik.errors.payment_method_uuid}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/*End::Form Group Transaction type */}

            {/*Begin::Form Group Transaction Reference */}
            <div className='fv-row'>
              <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                <span className='required'>Transaction Reference</span>
                <i
                  className='fas fa-exclamation-circle ms-2 fs-7'
                  data-bs-toggle='tooltip'
                  title='Enter transaction reference'
                ></i>
              </label>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Enter transaction reference'
                {...formik.getFieldProps('payment_reference')}
                name='payment_reference'
              />
              <div className='h-20px'>
                {formik.touched.payment_reference && formik.errors.payment_reference && (
                  <div className='fv-help-block color-red'>{formik.errors.payment_reference}</div>
                )}
              </div>
            </div>
            {/*End::Form Group Transaction Reference */}

            {/*Begin::Form Group property title */}
            {
              (rentDetails.proforma_invoice_link && formik.values.tenant_rent_uuid) &&
              <div className='fv-row mb-3'>
                <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                  <span className=''>Rent Transaction Receipt</span>
                  <i
                    className='fas fa-exclamation-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Upload rent transaction receipt'
                  ></i>
                </label>
                {/*begin::Dropzone field */}
                <div className='border border-2 rounded border-dashed row p-2 bg-white mx-2'>
                  <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                    <Dropzone onDrop={(accepted, rejected, event) => handleDropzoneRent(accepted, rejected, event)} maxFiles={1} accept={acceptedFormats}>
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                          {...getRootProps({
                            className:
                              'py-4 d-flex flex-column justify-content-center align-items-center',
                          })}
                        >
                          <input {...getInputProps()} />
                          {isDragActive ? (
                            <p>Drop the files here...</p>
                          ) : (
                            <p>Drag and drop files here, or click to select files</p>
                          )}
                          <i className='bi bi-file-earmark-arrow-up-fill fs-2x btn btn-active-color-primary py-0 mb-3'></i>
                          <span className='text-primary fs-7'>
                            Type: *.jpeg, *.jpg, *.png, *.pdf, *.doc or *.docx only
                          </span>
                          <span className='text-primary fs-7'>Max document:1</span>
                        </div>
                      )}
                    </Dropzone>
                  </section>
                </div>
                <aside className='px-5'>
                  {acceptedRentFiles.length > 0 && (
                    <div className='pt-2'>
                      <h4>Document is ready to upload</h4>
                      <ul><li>{acceptedRentFiles[0].name}</li></ul>
                    </div>
                  )}
                  {rejectedRentFiles.length > 0 && (
                    <div className='pt-2'>
                      <span className='fv-help-block color-red'>
                        Upload only one file or select files with allowed formats only
                      </span>
                      {/* <ul>{fileRejectionItems}</ul> */}
                    </div>
                  )}
                </aside>
                {/*end::Dropzone field */}
              </div>
            }
            {/*End::Form Group property title */}

            {/*Begin::Form Group property title */}
            {
              (rentDetails.utility_invoice_link && formik.values.tenant_utility_invoice_uuid) &&
              <div className='fv-row'>
                <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                  <span className=''>Utility Transaction Receipt</span>
                  <i
                    className='fas fa-exclamation-circle ms-2 fs-7'
                    data-bs-toggle='tooltip'
                    title='Upload utility transaction receipt'
                  ></i>
                </label>
                {/*begin::Dropzone field */}
                <div className='border border-2 rounded border-dashed row p-2 bg-white mx-2'>
                  <section className='border border-2 rounded border-primary border-dashed h-100 symbol bg-light-primary'>
                    <Dropzone onDrop={(accepted, rejected, event) => handleDropzoneUtility(accepted, rejected, event)} maxFiles={1} accept={acceptedFormats}>
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                          {...getRootProps({
                            className:
                              'py-4 d-flex flex-column justify-content-center align-items-center',
                          })}
                        >
                          <input {...getInputProps()} />
                          {isDragActive ? (
                            <p>Drop the files here...</p>
                          ) : (
                            <p>Drag and drop files here, or click to select files</p>
                          )}
                          <i className='bi bi-file-earmark-arrow-up-fill fs-2x btn btn-active-color-primary py-0 mb-3'></i>
                          <span className='text-primary fs-7'>
                            Type: *.jpeg, *.jpg, *.png, *.pdf, *.doc or *.docx only
                          </span>
                          <span className='text-primary fs-7'>Max document:1</span>
                        </div>
                      )}
                    </Dropzone>
                  </section>
                </div>
                <aside className='px-5'>
                  {acceptedUtilityFiles.length > 0 && (
                    <div className='pt-2'>
                      <h4>Document is ready to upload</h4>
                      <ul><li>{acceptedUtilityFiles[0].name}</li></ul>
                    </div>
                  )}
                  {rejectedUtilityFiles.length > 0 && (
                    <div className='pt-2'>
                      <span className='fv-help-block color-red'>
                        Upload only one file or select files with allowed formats only
                      </span>
                      {/* <ul>{fileRejectionItems}</ul> */}
                    </div>
                  )}
                </aside>
                {/*end::Dropzone field */}
              </div>
            }
            {/*End::Form Group property title */}
            {/*Begin::Actions */}
            <div className='d-flex flex-stack mt-7 justify-content-center'>
              {/* Begin:: Submit and next button div */}
              <div>
                {/* Begin:: submit button */}
                <button
                  type='submit'
                  className='btn btn-lg btn-primary'
                  // data-kt-stepper-action='submit'
                  disabled={submitBtnLoading}
                >
                  {submitBtnLoading ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Processing...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : (
                    'Submit'
                  )}
                </button>
                {/* Begin:: submit button */}
              </div>
              {/* Begin:: Submit and next button div */}
            </div>
            {/*end::Actions */}
          </form>
          {/*end::Form */}
        </div>
        {/*end:: Form Content */}
      </div >
      {/* End: Modal body */}
    </Modal >,
    modalsRoot
  )
}

export default MarkAsPaid
